// lib/rum/cookie-consent.ts — sync reader for the `zenopus_cc_v1` consent cookie.
//
// Lives in lib/rum/ so the early-load instrumentation hook
// (`instrumentation-client.ts`) can `import` it without dragging in the
// React hook (`hooks/useCookieConsent.ts`) and its useSyncExternalStore
// wiring. The contract:
//
//   - readConsentSync()   — returns the choices object, or null if no
//                           consent has been recorded yet. NEVER opt-in
//                           by default (DSGVO Art. 7).
//   - CONSENT_CHANGED_EVENT — name of the custom DOM event fired by the
//                           hook's persist() after a cookie write. Late-
//                           binding listeners (sentry/rum) subscribe to
//                           it so they can light up on accept-after-load
//                           without forcing a page reload.

export type CookieConsentChoices = {
  necessary: true;
  analytics: boolean;
  marketing: boolean;
  performance: boolean;
};

export const CONSENT_COOKIE_NAME = 'zenopus_cc_v1';

// Window event name. Use a namespaced string to avoid collisions with
// libraries that may dispatch generic `consent` / `cookieconsent` events.
export const CONSENT_CHANGED_EVENT = 'zenopus:cookieconsent:changed';

type Stored = {
  _v: 1;
  sessionId: string;
  choices: CookieConsentChoices;
  grantedAt: string;
};

function decodeBase64(input: string): string {
  if (typeof atob === 'function') return atob(input);
  if (typeof Buffer !== 'undefined') return Buffer.from(input, 'base64').toString('utf-8');
  throw new Error('no base64 decoder available');
}

function parseStored(raw: string | null): Stored | null {
  if (!raw) return null;
  try {
    const decoded = decodeBase64(decodeURIComponent(raw));
    const parsed = JSON.parse(decoded) as unknown;
    if (
      typeof parsed === 'object' &&
      parsed !== null &&
      'choices' in parsed &&
      typeof (parsed as { choices?: unknown }).choices === 'object' &&
      (parsed as { _v?: unknown })._v === 1
    ) {
      const stored = parsed as Stored;
      return {
        _v: 1,
        sessionId: typeof stored.sessionId === 'string' ? stored.sessionId : '',
        choices: {
          necessary: true,
          analytics: Boolean(stored.choices.analytics),
          marketing: Boolean(stored.choices.marketing),
          performance: Boolean(stored.choices.performance),
        },
        grantedAt: typeof stored.grantedAt === 'string' ? stored.grantedAt : '',
      };
    }
  } catch {
    // fall through — corrupted cookie counts as no consent (fail closed)
  }
  return null;
}

/**
 * Read the consent cookie synchronously from document.cookie.
 *
 * Returns `null` when no consent has been recorded yet. The default-deny
 * stance is mandatory under DSGVO Art. 7 — until the user opts in, no
 * non-necessary tracking may run.
 */
export function readConsentSync(): CookieConsentChoices | null {
  if (typeof document === 'undefined' || !document.cookie) return null;
  const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${CONSENT_COOKIE_NAME}=([^;]+)`));
  if (!match) return null;
  const stored = parseStored(match[1]);
  return stored?.choices ?? null;
}

/** Has the visitor accepted the `analytics` category? */
export function analyticsConsented(): boolean {
  return readConsentSync()?.analytics === true;
}

/** Has the visitor accepted the `performance` category? */
export function performanceConsented(): boolean {
  return readConsentSync()?.performance === true;
}

/** Has the visitor accepted the `marketing` category? */
export function marketingConsented(): boolean {
  return readConsentSync()?.marketing === true;
}
